import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { CalendarRange, Plus, X, Save, LifeBuoy, Settings2, GripVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function Monthly() {
  const { user } = useAuth();
  const isManager = ["atasan", "owner", "kepala_divisi", "super_admin"].includes(user?.role);
  const isAdmin = ["super_admin", "owner"].includes(user?.role);
  const [config, setConfig] = useState({ questions: [], plan_label: "Sasaran / Objektif", intro: "", support_enabled: true, self_rating_enabled: true });
  const [configOpen, setConfigOpen] = useState(false);
  const [data, setData] = useState(null);
  const [objectives, setObjectives] = useState([""]);
  const [answers, setAnswers] = useState({});
  const [support, setSupport] = useState([""]);
  const [rating, setRating] = useState(3);
  const questions = config.questions;

  const period = data?.period;
  const load = () => api.get("/monthly", { params: { scope: isManager ? "team" : "me" } }).then((r) => {
    setData(r.data);
    if (r.data.my_plan?.objectives?.length) setObjectives(r.data.my_plan.objectives);
    if (r.data.my_evaluation) {
      setAnswers(r.data.my_evaluation.answers || {});
      setSupport(r.data.my_evaluation.support_requests?.length ? r.data.my_evaluation.support_requests : [""]);
      setRating(r.data.my_evaluation.self_rating || 3);
    }
  });
  const loadConfig = () => api.get("/monthly/questions").then((r) => setConfig(r.data));
  useEffect(() => { loadConfig(); load(); }, []);

  const savePlan = async () => {
    await api.post("/monthly/plan", { period, objectives: objectives.filter((o) => o.trim()) });
    toast.success("Rencana bulanan disimpan"); load();
  };
  const saveEval = async () => {
    await api.post("/monthly/evaluation", { period, answers, support_requests: support.filter((s) => s.trim()), self_rating: rating });
    toast.success("Evaluasi bulanan dikirim"); load();
  };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><CalendarRange className="h-7 w-7 text-indigo-500" />Rencana & Evaluasi Bulanan</h1>
          <p className="text-slate-500 text-sm">Periode {period || "…"} · {config.intro || "siklus refleksi & permintaan dukungan"}</p>
        </div>
        {isAdmin && <Button variant="outline" onClick={() => setConfigOpen(true)} data-testid="monthly-config-btn"><Settings2 className="h-4 w-4 mr-1.5" />Konfigurasi</Button>}
      </div>

      <Tabs defaultValue="saya">
        <TabsList className="mb-5">
          <TabsTrigger value="saya" data-testid="monthly-tab-saya">Saya</TabsTrigger>
          {isManager && <TabsTrigger value="tim" data-testid="monthly-tab-tim">Tim</TabsTrigger>}
        </TabsList>

        <TabsContent value="saya">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Plan */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="monthly-plan-card">
              <p className="font-bold text-slate-700 mb-3">Rencana Kerja Bulan Ini</p>
              <Label className="text-xs">{config.plan_label || "Sasaran / Objektif"}</Label>
              {objectives.map((o, i) => (
                <div key={i} className="flex gap-2 mb-1.5 mt-1">
                  <Input value={o} onChange={(e) => setObjectives((a) => a.map((x, j) => j === i ? e.target.value : x))} placeholder={`Objektif ${i + 1}`} data-testid={`objective-input-${i}`} />
                  {objectives.length > 1 && <Button variant="ghost" size="icon" onClick={() => setObjectives((a) => a.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setObjectives((a) => [...a, ""])}><Plus className="h-3.5 w-3.5 mr-1" />Objektif</Button>
              <div className="mt-3"><Button size="sm" onClick={savePlan} data-testid="save-plan-btn" className="bg-indigo-600 hover:bg-indigo-700"><Save className="h-4 w-4 mr-1" />Simpan Rencana</Button></div>
            </div>

            {/* Evaluation */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="monthly-eval-card">
              <p className="font-bold text-slate-700 mb-3">Evaluasi Bulan Ini ({questions.length} Pertanyaan)</p>
              <div className="space-y-3">
                {questions.map((q) => (
                  <div key={q.id}>
                    <Label className="text-xs text-slate-600">{q.text}</Label>
                    <Textarea rows={2} value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} data-testid={`answer-${q.id}`} className="mt-1" />
                  </div>
                ))}
                <div className={config.support_enabled ? "" : "hidden"}>
                  <Label className="text-xs flex items-center gap-1"><LifeBuoy className="h-3.5 w-3.5" />Permintaan Dukungan (dikirim ke atasan)</Label>
                  {support.map((s, i) => (
                    <div key={i} className="flex gap-2 mb-1.5 mt-1"><Input value={s} onChange={(e) => setSupport((a) => a.map((x, j) => j === i ? e.target.value : x))} placeholder="Dukungan yang dibutuhkan" data-testid={`support-input-${i}`} />{support.length > 1 && <Button variant="ghost" size="icon" onClick={() => setSupport((a) => a.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}</div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setSupport((a) => [...a, ""])}><Plus className="h-3.5 w-3.5 mr-1" />Dukungan</Button>
                </div>
                <div className={config.self_rating_enabled ? "" : "hidden"}>
                  <Label className="text-xs">Penilaian Diri: <b className="text-indigo-600">{rating}/5</b></Label>
                  <Slider value={[rating]} onValueChange={(v) => setRating(v[0])} min={1} max={5} step={1} className="mt-2" data-testid="self-rating-slider" />
                </div>
                <Button size="sm" onClick={saveEval} data-testid="save-eval-btn" className="bg-indigo-600 hover:bg-indigo-700"><Save className="h-4 w-4 mr-1" />Kirim Evaluasi</Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {isManager && (
          <TabsContent value="tim">
            <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="monthly-team-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr><th className="text-left px-4 py-2.5">Anggota</th><th className="text-center px-3">Rencana</th><th className="text-center px-3">Evaluasi</th><th className="text-center px-3">Self-Rating</th><th className="text-left px-3">Permintaan Dukungan</th></tr>
                </thead>
                <tbody>
                  {(data?.plans || []).concat((data?.evaluations || []).filter((e) => !(data?.plans || []).some((p) => p.user_id === e.user_id)).map((e) => ({ user_id: e.user_id, user_name: e.user_name, objectives: [] })))
                    .map((row) => {
                      const ev = (data?.evaluations || []).find((e) => e.user_id === row.user_id);
                      return (
                        <tr key={row.user_id} className="border-t border-slate-100" data-testid={`monthly-row-${row.user_id}`}>
                          <td className="px-4 py-2.5 font-medium text-slate-700">{row.user_name}</td>
                          <td className="text-center">{row.objectives?.length ? <span className="text-emerald-600">✓ {row.objectives.length}</span> : <span className="text-slate-300">–</span>}</td>
                          <td className="text-center">{ev ? <span className="text-emerald-600">✓</span> : <span className="text-slate-300">–</span>}</td>
                          <td className="text-center">{ev?.self_rating || "–"}</td>
                          <td className="px-3 text-xs text-amber-700">{ev?.support_requests?.join("; ") || "–"}</td>
                        </tr>
                      );
                    })}
                  {(data?.plans || []).length === 0 && (data?.evaluations || []).length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-8">Belum ada data tim bulan ini.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {isAdmin && <MonthlyConfigDialog open={configOpen} onOpenChange={setConfigOpen} config={config} onSaved={(c) => { setConfig(c); setConfigOpen(false); }} />}
    </div>
  );
}

function MonthlyConfigDialog({ open, onOpenChange, config, onSaved }) {
  const [qs, setQs] = useState([]);
  const [planLabel, setPlanLabel] = useState("");
  const [intro, setIntro] = useState("");
  const [support, setSupport] = useState(true);
  const [selfRating, setSelfRating] = useState(true);

  useEffect(() => {
    if (!open) return;
    setQs((config.questions || []).map((q) => ({ ...q })));
    setPlanLabel(config.plan_label || "Sasaran / Objektif");
    setIntro(config.intro || "");
    setSupport(config.support_enabled !== false);
    setSelfRating(config.self_rating_enabled !== false);
  }, [open, config]);

  const save = async () => {
    const clean = qs.filter((q) => (q.text || "").trim());
    if (!clean.length) return toast.error("Minimal satu pertanyaan evaluasi");
    try {
      const r = await api.put("/monthly/config", {
        questions: clean.map((q, i) => ({ id: q.id || `q${i + 1}`, text: q.text.trim() })),
        plan_label: planLabel, intro, support_enabled: support, self_rating_enabled: selfRating,
      });
      toast.success("Konfigurasi evaluasi disimpan");
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto zoa-scroll" aria-describedby={undefined} data-testid="monthly-config-dialog">
        <DialogHeader><DialogTitle>Konfigurasi Rencana & Evaluasi</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Label Bagian Rencana</Label><Input value={planLabel} onChange={(e) => setPlanLabel(e.target.value)} data-testid="config-plan-label" /></div>
          <div className="space-y-1.5"><Label>Deskripsi/Intro (subjudul halaman)</Label><Input value={intro} onChange={(e) => setIntro(e.target.value)} data-testid="config-intro" /></div>
          <div>
            <Label className="text-xs font-semibold text-slate-500 uppercase">Pertanyaan Evaluasi</Label>
            <div className="space-y-2 mt-2">
              {qs.map((q, i) => (
                <div key={i} className="flex gap-2 items-start" data-testid={`config-q-${i}`}>
                  <GripVertical className="h-4 w-4 text-slate-300 mt-2.5 shrink-0" />
                  <Textarea rows={2} value={q.text} onChange={(e) => setQs((a) => a.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} data-testid={`config-q-input-${i}`} />
                  <Button variant="ghost" size="icon" onClick={() => setQs((a) => a.filter((_, j) => j !== i))} data-testid={`config-q-remove-${i}`}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => setQs((a) => [...a, { id: `q${a.length + 1}_${Date.now()}`, text: "" }])} data-testid="config-add-q"><Plus className="h-3.5 w-3.5 mr-1" />Tambah Pertanyaan</Button>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <Label>Aktifkan Permintaan Dukungan</Label>
            <Switch checked={support} onCheckedChange={setSupport} data-testid="config-support-switch" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <Label>Aktifkan Penilaian Diri (1–5)</Label>
            <Switch checked={selfRating} onCheckedChange={setSelfRating} data-testid="config-self-rating-switch" />
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button><Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700" data-testid="config-save-btn">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
